import { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface PortfolioItem {
  id: string;
  title: string;
  category: string;
  image_url: string;
  description: string;
  link: string;
  created_at: string;
}

// Default web design portfolio items
const defaultPortfolio: Omit<PortfolioItem, 'id' | 'created_at'>[] = [
  {
    title: 'Modern E-commerce Platform',
    category: 'E-commerce',
    image_url: 'https://readdy.ai/api/search-image?query=modern%20ecommerce%20website%20design%20interface%20clean%20minimalist%20layout%20product%20grid%20shopping%20cart%20checkout%20white%20background%20professional%20web%20design%20mockup%20desktop%20view%20high%20quality%20digital%20interface&width=800&height=600&seq=ecom2024&orientation=landscape',
    description: 'Full-featured online store with product catalog, shopping cart, secure checkout, and customer accounts',
    link: ''
  },
  {
    title: 'Corporate Business Website',
    category: 'Business',
    image_url: 'https://readdy.ai/api/search-image?query=professional%20corporate%20business%20website%20design%20clean%20modern%20layout%20about%20services%20team%20contact%20sections%20white%20background%20elegant%20web%20interface%20desktop%20mockup%20high%20quality%20digital%20design&width=800&height=600&seq=corp2024&orientation=landscape',
    description: 'Professional business website showcasing services, team members, company values, and client testimonials',
    link: ''
  },
  {
    title: 'Restaurant & Cafe Website',
    category: 'Small Shops',
    image_url: 'https://readdy.ai/api/search-image?query=modern%20restaurant%20cafe%20website%20design%20menu%20showcase%20online%20ordering%20reservation%20system%20location%20map%20white%20background%20appetizing%20food%20photography%20web%20interface%20desktop%20view%20high%20quality&width=800&height=600&seq=rest2024&orientation=landscape',
    description: 'Restaurant website with online menu, reservation system, photo gallery, and delivery integration',
    link: ''
  },
  {
    title: 'Healthcare Clinic Portal',
    category: 'Clinics',
    image_url: 'https://readdy.ai/api/search-image?query=modern%20healthcare%20medical%20clinic%20website%20design%20appointment%20booking%20doctor%20profiles%20services%20patient%20portal%20clean%20white%20background%20professional%20medical%20web%20interface%20desktop%20mockup%20high%20quality&width=800&height=600&seq=health2024&orientation=landscape',
    description: 'Medical clinic website with online appointment booking, doctor profiles, services, and patient resources',
    link: ''
  },
  {
    title: 'Professional Portfolio Site',
    category: 'Portfolio',
    image_url: 'https://readdy.ai/api/search-image?query=creative%20portfolio%20website%20design%20showcase%20projects%20work%20gallery%20about%20contact%20minimalist%20clean%20white%20background%20modern%20web%20interface%20desktop%20view%20high%20quality%20digital%20design&width=800&height=600&seq=port2024&orientation=landscape',
    description: 'Creative portfolio showcasing projects, skills, experience, and contact information for freelancers',
    link: ''
  },
  {
    title: 'Real Estate Listings',
    category: 'Business',
    image_url: 'https://readdy.ai/api/search-image?query=modern%20real%20estate%20property%20listing%20website%20design%20search%20filters%20property%20cards%20map%20view%20virtual%20tours%20white%20background%20professional%20web%20interface%20desktop%20mockup%20high%20quality&width=800&height=600&seq=real2024&orientation=landscape',
    description: 'Real estate platform with property listings, advanced search, virtual tours, and agent contacts',
    link: ''
  },
  {
    title: 'Fitness & Wellness Center',
    category: 'Coaches & Educators',
    image_url: 'https://readdy.ai/api/search-image?query=modern%20fitness%20gym%20wellness%20website%20design%20class%20schedules%20membership%20plans%20trainer%20profiles%20online%20booking%20energetic%20white%20background%20professional%20web%20interface%20desktop%20view%20high%20quality&width=800&height=600&seq=fit2024&orientation=landscape',
    description: 'Fitness center website with class schedules, membership options, trainer profiles, and online booking',
    link: ''
  },
  {
    title: 'Legal Services Firm',
    category: 'Consultants',
    image_url: 'https://readdy.ai/api/search-image?query=professional%20law%20firm%20legal%20services%20website%20design%20practice%20areas%20attorney%20profiles%20case%20results%20consultation%20booking%20elegant%20white%20background%20trustworthy%20web%20interface%20desktop%20mockup%20high%20quality&width=800&height=600&seq=legal2024&orientation=landscape',
    description: 'Law firm website featuring practice areas, attorney bios, case results, and consultation scheduling',
    link: ''
  },
  {
    title: 'Educational Platform',
    category: 'Coaches & Educators',
    image_url: 'https://readdy.ai/api/search-image?query=modern%20online%20learning%20education%20platform%20website%20design%20course%20catalog%20student%20dashboard%20video%20lessons%20progress%20tracking%20clean%20white%20background%20professional%20web%20interface%20desktop%20view%20high%20quality&width=800&height=600&seq=edu2024&orientation=landscape',
    description: 'Online learning platform with course catalog, student dashboard, video lessons, and progress tracking',
    link: ''
  },
  {
    title: 'Charity Organization',
    category: 'Non-Profits',
    image_url: 'https://readdy.ai/api/search-image?query=modern%20nonprofit%20charity%20organization%20website%20design%20donation%20platform%20volunteer%20opportunities%20impact%20stories%20mission%20statement%20inspiring%20white%20background%20compassionate%20web%20interface%20desktop%20mockup%20high%20quality&width=800&height=600&seq=charity2024&orientation=landscape',
    description: 'Non-profit website with donation platform, volunteer sign-up, impact stories, and mission statement',
    link: ''
  },
  {
    title: 'Photography Studio',
    category: 'Portfolio',
    image_url: 'https://readdy.ai/api/search-image?query=professional%20photography%20studio%20website%20design%20portfolio%20gallery%20booking%20system%20pricing%20packages%20services%20showcase%20minimalist%20white%20background%20elegant%20web%20interface%20desktop%20view%20high%20quality&width=800&height=600&seq=photo2024&orientation=landscape',
    description: 'Photography studio website with portfolio gallery, booking system, pricing packages, and client reviews',
    link: ''
  },
  {
    title: 'Tech Startup Landing',
    category: 'Landing Page',
    image_url: 'https://readdy.ai/api/search-image?query=modern%20tech%20startup%20landing%20page%20website%20design%20product%20features%20pricing%20testimonials%20call%20to%20action%20innovative%20white%20background%20sleek%20web%20interface%20desktop%20mockup%20high%20quality%20digital%20design&width=800&height=600&seq=tech2024&orientation=landscape',
    description: 'Startup landing page with product features, pricing tiers, customer testimonials, and sign-up forms',
    link: ''
  }
];

export default function Portfolio() {
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const { data, error } = await supabase
        .from('portfolio_images')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // If database is empty, use default portfolio
      if (!data || data.length === 0) {
        setPortfolioItems(defaultPortfolio.map((item, index) => ({
          ...item,
          id: `default-${index}`,
          created_at: new Date().toISOString()
        })));
      } else {
        setPortfolioItems(data);
      }
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      // Fallback to default portfolio on error
      setPortfolioItems(defaultPortfolio.map((item, index) => ({
        ...item,
        id: `default-${index}`,
        created_at: new Date().toISOString()
      })));
    } finally {
      setLoading(false);
    }
  };

  const categories = ['All', ...Array.from(new Set(portfolioItems.map(item => item.category)))];
  
  const filteredItems = selectedCategory === 'All' 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === selectedCategory);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (loading) {
    return (
      <section id="portfolio" className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="animate-pulse">
            <div className="h-8 sm:h-10 bg-gray-200 rounded w-48 sm:w-64 mx-auto mb-4"></div>
            <div className="h-4 sm:h-6 bg-gray-200 rounded w-64 sm:w-96 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="portfolio" className="py-16 sm:py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-100 text-purple-700 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
            <i className="ri-gallery-line"></i>
            Our Work
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-4">
            Recent{' '}
            <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              Projects
            </span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            See how we've helped businesses like yours succeed online with beautiful, functional websites
          </p>
        </div>

        {/* Category Filter */}
        {categories.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10 sm:mb-12 px-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {/* Portfolio Grid */}
        {filteredItems.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="group relative bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                {/* Image */}
                <div className="relative h-64 sm:h-72 lg:h-80 overflow-hidden">
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Overlay Content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-4 sm:p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="inline-block px-2 sm:px-3 py-1 bg-cyan-500 text-white text-xs sm:text-sm rounded-full mb-2 sm:mb-3 w-fit">
                      {item.category}
                    </span>
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">
                      {item.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-200 mb-3 sm:mb-4 line-clamp-2">
                      {item.description}
                    </p>
                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-white text-xs sm:text-sm font-semibold hover:gap-3 transition-all cursor-pointer"
                      >
                        View Project
                        <i className="ri-arrow-right-line"></i>
                      </a>
                    )}
                  </div>
                </div>

                {/* Card Footer */}
                <div className="p-4 sm:p-6">
                  <span className="inline-block px-2 sm:px-3 py-1 bg-gray-100 text-gray-700 text-xs sm:text-sm rounded-full mb-2 sm:mb-3">
                    {item.category}
                  </span>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 sm:py-16 lg:py-20">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <i className="ri-gallery-line text-3xl sm:text-4xl text-gray-400"></i>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 px-4">No Projects Yet</h3>
            <p className="text-sm sm:text-base text-gray-600 px-4">Check back soon to see our latest work!</p>
          </div>
        )}

        {/* CTA */}
        <div className="text-center">
          <button 
            onClick={scrollToContact}
            className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm sm:text-base font-semibold rounded-full hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Start Your Project
          </button>
        </div>
      </div>
    </section>
  );
}
